<template>
  <div
    class="fts-panel-divider"
    :class="{ 'fts-panel-divider--dragging': dragging }"
    role="separator"
    aria-orientation="vertical"
    :aria-valuemin="presets[0]"
    :aria-valuemax="presets[presets.length - 1]"
    :aria-valuenow="Math.round(width)"
    :aria-label="ariaLabel"
    tabindex="0"
    @pointerdown="onPointerDown"
    @keydown="onKeyDown"
  >
    <div class="fts-panel-divider__handle" />
  </div>
</template>

<script>
/**
 * Draggable, keyboard-accessible divider between two layout panels. Free-follows the pointer
 * while dragging and snaps to the closest entry in `presets` on release (SS-panel-resize).
 * Purely presentational: the parent owns the panel's actual width and persists it, this
 * component only reports pointer/keyboard intent via events.
 */
export default {
  name: 'FtsPanelDivider',
  props: {
    presets: {
      // Ascending list of snap widths (px) for the panel this divider controls.
      type: Array,
      required: true
    },
    width: {
      // Current live width (px) of the panel this divider controls.
      type: Number,
      required: true
    },
    invert: {
      // true when the controlled panel sits to the left of the divider (dragging right shrinks it).
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: 'Resize panel'
    }
  },
  data() {
    return {
      dragging: false,
      startX: 0,
      startWidth: 0
    };
  },
  computed: {
    min() {
      return this.presets[0];
    },
    max() {
      return this.presets[this.presets.length - 1];
    }
  },
  methods: {
    clamp(value) {
      return Math.min(this.max, Math.max(this.min, value));
    },
    closestPreset(value) {
      return this.presets.reduce((closest, preset) =>
        Math.abs(preset - value) < Math.abs(closest - value) ? preset : closest
      , this.presets[0]);
    },
    onPointerDown(evt) {
      if (evt.button !== undefined && evt.button !== 0) return;
      this.dragging = true;
      this.startX = evt.clientX;
      this.startWidth = this.width;
      if (evt.target.setPointerCapture) {
        evt.target.setPointerCapture(evt.pointerId);
      }
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      this.$emit('drag-start');
    },
    onPointerMove(evt) {
      if (!this.dragging) return;
      const deltaX = evt.clientX - this.startX;
      const applied = this.invert ? -deltaX : deltaX;
      this.$emit('update:width', this.clamp(this.startWidth + applied));
    },
    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      this.$emit('snap', this.closestPreset(this.width));
      this.$emit('drag-end');
    },
    onKeyDown(evt) {
      const index = this.presets.indexOf(this.closestPreset(this.width));
      let target;
      if (evt.key === 'ArrowLeft' || evt.key === 'ArrowDown') {
        target = this.presets[Math.max(0, index - 1)];
      } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowUp') {
        target = this.presets[Math.min(this.presets.length - 1, index + 1)];
      } else if (evt.key === 'Home') {
        target = this.presets[0];
      } else if (evt.key === 'End') {
        target = this.presets[this.presets.length - 1];
      } else {
        return;
      }
      evt.preventDefault();
      this.$emit('update:width', target);
      this.$emit('snap', target);
    }
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }
};
</script>

<style lang="scss" scoped>
.fts-panel-divider {
  position: relative;
  flex: 0 0 6px;
  align-self: stretch;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  outline: none;
  box-shadow: none;
  background-color: var(--fts-card-background);
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: translateX(-50%);
    background-color: var(--fts-border);
    box-shadow: none;
    transition: background-color 150ms ease-out, width 150ms ease-out;
  }
  &:hover::after,
  &:focus-visible::after {
    width: 2px;
    background-color: var(--fts-gray-300);
  }
  &--dragging::after {
    width: 2px;
    background-color: var(--fts-gray-300);
    transition: none;
  }
}
</style>
